import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Landmark, Banknote, RefreshCw, FileText } from 'lucide-react';
import { Button } from '../../ui/button';
import { DateRangePicker, rangeQuery } from '../common/DateRangePicker';
import { eur } from '../ExpenseDetail/DetailKpis';
import { MethodsManager } from './MethodsManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');
const fileHref = (u) => (u?.startsWith('/api/') ? `${API_URL}${u}` : u);

const MethodCard = ({ m, active, onClick }) => {
  const Icon = m.kind === 'cash' ? Banknote : Landmark;
  return (
    <button onClick={onClick} data-testid={`bank-card-${m.id}`}
      className={`text-left p-4 rounded-xl border transition-colors ${active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50'}`}>
      <div className="flex items-center gap-2 text-sm font-medium"><Icon size={16} />{m.name}</div>
      <p className="text-2xl font-bold mt-2" data-testid={`bank-total-${m.id}`}>{eur(m.total)}</p>
      <p className={`text-xs mt-0.5 ${active ? 'text-gray-300' : 'text-gray-500'}`}>{m.count} pago{m.count === 1 ? '' : 's'}{m.last_paid ? ` · último ${fmtDate(m.last_paid)}` : ''}</p>
    </button>
  );
};

const Movements = ({ data }) => (
  <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="bank-movements">
    <div className="px-4 py-3 border-b bg-gray-50 flex justify-between text-sm">
      <span className="font-semibold text-gray-900">Pagos con {data.method.name}</span>
      <span className="font-bold">{eur(data.total)}</span>
    </div>
    {data.rows.length === 0 && <p className="text-center text-gray-400 py-10 text-sm">Sin pagos en este periodo</p>}
    <div className="divide-y">
      {data.rows.map(r => (
        <div key={r.id} className="px-4 py-2.5 flex items-center gap-3 text-sm" data-testid={`bank-movement-${r.id}`}>
          <span className="w-20 shrink-0 text-gray-500 text-xs">{fmtDate(r.paid_date)}</span>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{r.vendor || r.description || '—'}</p>
            <p className="text-xs text-gray-400 truncate">Factura {r.invoice_number || 's/n'} del {fmtDate(r.date)}</p>
          </div>
          <span className="font-semibold shrink-0">{eur(r.amount)}</span>
          {r.invoice_url && !r.invoice_url.startsWith('/api/uploads/')
            ? <a href={fileHref(r.invoice_url)} target="_blank" rel="noreferrer" className="p-1.5 text-blue-500 hover:text-blue-700" title="Ver factura"><FileText size={15} /></a>
            : <span className="w-7" />}
        </div>
      ))}
    </div>
  </div>
);

export default function BanksPanel() {
  const navigate = useNavigate();
  const [range, setRange] = useState({ from: '', to: '' });
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [movements, setMovements] = useState(null);

  const loadSummary = useCallback(() => {
    fetch(`${API_URL}/api/admin/bancos/resumen?${rangeQuery(range)}`, { headers: auth() })
      .then(r => (r.ok ? r.json() : null)).then(setSummary);
  }, [range]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  useEffect(() => {
    if (!selected) return setMovements(null);
    fetch(`${API_URL}/api/admin/bancos/${selected}/movimientos?${rangeQuery(range)}`, { headers: auth() })
      .then(r => (r.ok ? r.json() : null)).then(setMovements);
  }, [selected, range]);

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6" data-testid="banks-panel">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/gastos')} data-testid="banks-back-btn"><ArrowLeft size={20} /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Pagos</h1>
          <p className="text-sm text-gray-500">{summary ? `${eur(summary.total)} pagados en el periodo` : 'Cargando…'}</p>
        </div>
      </div>
      <div className="bg-white rounded-xl border shadow-sm p-3">
        <DateRangePicker value={range} onChange={setRange} onClear={() => setRange({ from: '', to: '' })} idPrefix="banks-range" />
      </div>
      {!summary && <div className="flex justify-center py-12"><RefreshCw className="animate-spin text-gray-400" /></div>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="bank-cards">
        {summary?.methods.map(m => <MethodCard key={m.id} m={m} active={selected === m.id} onClick={() => setSelected(selected === m.id ? null : m.id)} />)}
      </div>
      {movements && <Movements data={movements} />}
      {summary && <MethodsManager methods={summary.methods} onChange={loadSummary} />}
    </div>
  );
}
